import SEO from "@/lib/components/SEO";
import axios from "axios";
import {
  getAllClassrooms,
  getClassroom,
  getClassroomPreview,
} from "lib/common/endpoints/public";
import { Classroom } from "lib/common/types/classroom";
import Registration from "lib/components/classroomRegister/Registration";
import Overview from "lib/components/classroomRegister/Overview";
import ImportKatex from "lib/components/ImportKatex";
import { GetStaticProps } from "next";
import { breadcrumbLd, courseLd } from "lib/seo/jsonLd";
import { useAppDispatch } from "redux/hooks";
import { showSignInDrawer } from "redux/reducers/drawerSlice";
import {
  Warning,
  Clock,
  PlayCircle,
  CalendarBlank,
  X,
  CaretRight,
  CaretDown,
  ShieldCheck,
} from "@phosphor-icons/react";
import Image from "next/image";
import Link from "next/link";
import Custom404 from "pages/404";
import { defaultLayout } from "pages/layout/default";
import React, { useEffect, useRef, useState } from "react";
import { imgUrl } from "utils/constants";
import { createClassroomSlug, extractIdFromSlug } from "utils/slugify";
import { getVideoPreviewUrl } from "lib/common/endpoints/public";
import dynamic from "next/dynamic";

const PreviewVideo = dynamic(
  () => import("lib/components/Video/PreviewVideo"),
  { ssr: false },
);

const siteUrl = "https://clamphook.com";

// Safely resolve media URL — avoid double-prepending imgUrl when the path is already absolute
const toMediaUrl = (path?: string | null): string | null => {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return imgUrl + (path.startsWith("/") ? path.slice(1) : path);
};

// Search-facing title/description for classrooms whose product name doesn't
// match what students search for. Keyed by classroom id; the on-page name is
// unchanged.
const SEO_OVERRIDES: Record<string, { title: string; description: string }> = {
  // Clamphook: Pre Engineering 2083 (Grade XII) Online
  "6a40fbfd067c38f5afcd63ce": {
    title: "IOE Entrance Preparation Online 2083 | Clamphook",
    description:
      "IOE entrance preparation online for the 2083 batch: live and recorded classes, weekly CBT mock tests, and mentors from Pulchowk. Join from anywhere in Nepal.",
  },
};

// Extract a plain string sessionId regardless of whether API returns string or { $oid }
const toSessionId = (id: any): string => {
  if (typeof id === "string") return id;
  if (id && typeof id === "object" && "$oid" in id) return id.$oid;
  return String(id);
};

const getClassroomId = (
  classroom: { _id?: string | { $oid?: string } } | null | undefined,
) => {
  if (!classroom?._id) return "";
  if (typeof classroom._id === "string") return classroom._id;
  return classroom._id.$oid ?? "";
};

const normalizeClassroom = (classroom: any): Classroom | null => {
  if (!classroom) return null;
  return { ...classroom, _id: { $oid: getClassroomId(classroom) } };
};

const formatDate = (value: any) => {
  if (!value) return null;
  const raw = value?.$date ?? value;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

function fmtDuration(ms: number) {
  if (!ms) return "";
  const secs = ms / 1000; // durationSeconds is actually milliseconds
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

interface PreviewMeta {
  totalSessions: number;
  freePreviewCount: number;
  totalHours: string;
}

interface Batch {
  title: string;
  startTime: number;
  sessions: {
    sessionId: string;
    title: string;
    durationSeconds: number;
    isFreePreview: boolean;
    hasRecording: boolean;
  }[];
}

// ── Preview video modal ────────────────────────────────────────────────────
function VideoModal({
  sessionId,
  title,
  onClose,
}: {
  sessionId: string;
  title: string;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative z-10 w-full max-w-3xl bg-gray-900 dark:bg-black rounded-2xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 bg-primary-900">
          <p className="text-xs font-bold text-primary-300">
            Free Preview
          </p>
          <div className="flex-1 px-4 min-w-0">
            <p className="text-sm font-semibold text-white truncate">{title}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 w-7 h-7 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
            aria-label="Close"
          >
            <X size={13} />
          </button>
        </div>
        <PreviewVideo sessionId={sessionId} />
      </div>
    </div>
  );
}

// ── Module accordion for sidebar ──────────────────────────────────────────
function ModuleAccordion({
  curriculum,
  onPreview,
}: {
  curriculum: Batch[];
  onPreview: (sessionId: string, title: string) => void;
}) {
  const [openBatch, setOpenBatch] = useState<number | null>(null);
  return (
    <>
      {curriculum.map((batch, bi) => {
        const isOpen = openBatch === bi;
        const batchDuration = batch.sessions.reduce(
          (s, se) => s + (se.durationSeconds ?? 0), 0,
        );
        return (
          <div key={bi} className="border-b border-gray-50 dark:border-gray-700/50 last:border-b-0">
            <button
              type="button"
              onClick={() => setOpenBatch(isOpen ? null : bi)}
              className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left"
            >
              <div
                className="shrink-0 w-6 h-6 flex items-center justify-center text-xs font-semibold"
                style={{
                  background: bi === 0 ? "#3b69fc" : "#eef2ff",
                  color: bi === 0 ? "#fff" : "#3b69fc",
                  borderRadius: "50%",
                }}
              >
                {bi + 1}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 leading-snug line-clamp-2">
                  {batch.title}
                </p>
                {batchDuration > 0 && (
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                    {fmtDuration(batchDuration)} · {batch.sessions.length} sessions
                  </p>
                )}
              </div>
              <CaretDown
                size={11}
                className={`shrink-0 text-gray-600 dark:text-gray-400 transition-transform duration-150 ${isOpen ? "rotate-180" : ""}`}
              />
            </button>

            {isOpen && (
              <div className="bg-gray-50 dark:bg-gray-800/60 divide-y divide-gray-100 dark:divide-gray-700/50">
                {batch.sessions.map((session) => {
                  const canPreview = session.isFreePreview && session.hasRecording;
                  return (
                    <div key={session.sessionId} className="flex items-center gap-2.5 pl-10 pr-4 py-2.5">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-700 dark:text-gray-300 leading-snug line-clamp-2">
                          {session.title}
                        </p>
                        {session.durationSeconds > 0 && (
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                            {fmtDuration(session.durationSeconds)}
                          </p>
                        )}
                      </div>
                      {canPreview && (
                        <button
                          type="button"
                          onClick={() => onPreview(session.sessionId, session.title)}
                          className="shrink-0 text-xs font-bold text-primary dark:text-primary-300 hover:text-primary-600 bg-primary-50 dark:bg-primary-900/30 hover:bg-primary-100 dark:hover:bg-primary-900/50 px-2 py-1 rounded-lg transition-colors"
                        >
                          Preview
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

// ── Enroll modal (mobile) ──────────────────────────────────────────────────
function EnrollModal({
  classRoom,
  onClose,
  onSignIn,
}: {
  classRoom: Classroom;
  onClose: () => void;
  onSignIn: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative z-10 w-full bg-white dark:bg-gray-900 max-h-[90vh] flex flex-col overflow-hidden rounded-t-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-1 bg-primary shrink-0" />
        <div className="flex items-center justify-between px-5 py-3 bg-primary-50 dark:bg-primary-900/20 border-b border-gray-100 dark:border-gray-700 shrink-0">
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Enroll Now</p>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-800 transition-colors"
            aria-label="Close"
          >
            <X size={13} />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 px-5 pb-8 pt-4">
          <Registration
            title="Registration Form"
            classRoom={classRoom}
            onSignIn={onSignIn}
          />
        </div>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────
const ClassRoomPage = ({
  classRoom,
  previewMeta,
  initialCurriculum,
}: {
  classRoom: Classroom | null;
  previewMeta: PreviewMeta | null;
  initialCurriculum: Batch[];
}) => {
  const dispatch = useAppDispatch();
  // Single state drives inline banner playback for both banner button and module previews
  const [playingSession, setPlayingSession] = useState<{
    sessionId: string;
    title: string;
  } | null>(null);
  const [curriculum, setCurriculum] = useState<Batch[]>(initialCurriculum ?? []);
  const [curriculumLoaded, setCurriculumLoaded] = useState((initialCurriculum ?? []).length > 0);
  const registrationRef = useRef<HTMLDivElement>(null);
  const mobileRegRef = useRef<HTMLDivElement>(null);
  const bannerRef = useRef<HTMLDivElement>(null);

  const handleSignIn = () => {
    dispatch(showSignInDrawer());
  };

  const scrollToEnroll = () =>
    mobileRegRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const playPreview = (sessionId: string, title: string) => {
    setPlayingSession({ sessionId, title });
    bannerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const classroomId = classRoom ? getClassroomId(classRoom) : "";

  useEffect(() => {
    if (!classroomId) return;
    axios
      .get(`${getClassroomPreview}${classroomId}/preview`)
      .then((r) => {
        if (r.data?.success) setCurriculum(r.data.data.curriculum ?? []);
      })
      .catch(() => {})
      .finally(() => setCurriculumLoaded(true));
  }, [classroomId]);

  if (!classRoom) return <Custom404 />;

  const startDate = formatDate(classRoom.startDate);
  const heroBanner =
    toMediaUrl(classRoom.banner) ??
    toMediaUrl(classRoom.thumbnail) ??
    "/img/Clamphook.png";
  const heroLogo = toMediaUrl(classRoom.logo) ?? "/img/IOE.png";

  const canonicalUrl = `${siteUrl}/classrooms/${createClassroomSlug(
    classRoom.name,
    classroomId,
  )}`;
  const seoOverride = SEO_OVERRIDES[classroomId];
  const metaDescription =
    seoOverride?.description ??
    `Prepare for IOE / CSIT entrance with ${classRoom.name} on Clamphook. Live classes, weekly CBT mock tests, and structured notes.`;

  const jsonLd = [
    breadcrumbLd([
      { name: "Home", url: "/" },
      { name: "Courses", url: "/classrooms" },
      { name: classRoom.name, url: canonicalUrl },
    ]),
    courseLd({
      name: classRoom.name,
      description: metaDescription,
      url: canonicalUrl,
      image: heroBanner,
      price:
        classRoom.discountedPrice ??
        classRoom.initialPrice ??
        (classRoom.fee ? Number(classRoom.fee) : undefined),
    }),
  ];

  const priceDisplay = classRoom.discountedPrice
    ? `NPR ${classRoom.discountedPrice.toLocaleString("en-IN")}`
    : classRoom.initialPrice
      ? `NPR ${classRoom.initialPrice.toLocaleString("en-IN")}`
      : classRoom.fee
        ? `Rs. ${classRoom.fee}`
        : null;

  const originalPrice =
    classRoom.discountedPrice && classRoom.initialPrice
      ? `NPR ${classRoom.initialPrice.toLocaleString("en-IN")}`
      : null;

  const scrollToReg = () =>
    registrationRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });

  // Flatten all preview-able sessions for the free preview count
  const allSessions = curriculum.flatMap((b) => b.sessions);
  const freePreviews = allSessions.filter(
    (s) => s.isFreePreview && s.hasRecording,
  );

  return (
    <>
      <SEO
        title={seoOverride?.title ?? `${classRoom.name} | Clamphook`}
        description={metaDescription}
        canonicalUrl={canonicalUrl}
        ogType="website"
        ogImage={heroBanner}
        keywords={[classRoom.name, "IOE entrance", "CSIT entrance"]}
        jsonLd={jsonLd}
      />
      <ImportKatex />

      <div className="bg-white dark:bg-gray-900 min-h-screen pb-20 lg:pb-0">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-6 sm:py-10">

          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400 mb-6 flex-wrap">
            <Link href="/" className="hover:text-primary dark:hover:text-primary-300 no-underline text-gray-600 dark:text-gray-400 transition-colors">
              Home
            </Link>
            <CaretRight size={9} />
            <Link href="/classrooms" className="hover:text-primary dark:hover:text-primary-300 no-underline text-gray-600 dark:text-gray-400 transition-colors">
              Courses
            </Link>
            <CaretRight size={9} />
            <span className="text-gray-600 dark:text-gray-300 truncate max-w-[200px]">
              {classRoom.name}
            </span>
          </nav>

          <div className="grid lg:grid-cols-[1fr_340px] gap-8 lg:gap-12 items-start">

            {/* ══ LEFT COLUMN ══════════════════════════════════════════════ */}
            <div className="min-w-0">

              {/* Banner / inline preview video */}
              <div ref={bannerRef} className="relative aspect-video bg-gray-900 rounded-2xl overflow-hidden mb-6 group scroll-mt-4">
                {playingSession ? (
                  <>
                    <PreviewVideo sessionId={toSessionId(playingSession.sessionId)} />
                    <button
                      type="button"
                      onClick={() => setPlayingSession(null)}
                      className="absolute top-3 right-3 z-10 w-7 h-7 flex items-center justify-center rounded-lg bg-black/60 hover:bg-black/80 text-white transition-colors"
                      aria-label="Close preview"
                    >
                      <X size={13} />
                    </button>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-4 py-3">
                      <p className="text-xs font-bold text-primary-300">Free Preview</p>
                      <p className="text-xs font-semibold text-white truncate">{playingSession.title}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <Image
                      src={heroBanner}
                      alt={classRoom.name}
                      fill
                      className="object-cover"
                      priority
                      unoptimized={/^https?:\/\//.test(heroBanner) && !heroBanner.includes("assets.clamphook.com")}
                    />
                    {freePreviews.length > 0 && (
                      <div className="absolute inset-0 bg-primary-900/30 flex items-center justify-center group-hover:bg-primary-900/50 transition-colors duration-200">
                        <button
                          type="button"
                          onClick={() => playPreview(freePreviews[0].sessionId, freePreviews[0].title)}
                          className="flex items-center gap-2 bg-white text-primary font-bold text-sm px-5 py-2.5 rounded-xl shadow-xl hover:bg-primary-50 transition-colors"
                        >
                          <PlayCircle size={18} weight="fill" />
                          Watch Preview
                        </button>
                      </div>
                    )}
                    {previewMeta?.totalHours && (
                      <div className="absolute bottom-3 right-3 bg-black/70 text-white text-xs font-semibold px-2 py-1 rounded-lg flex items-center gap-1">
                        <Clock size={11} />
                        {previewMeta.totalHours}h
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Title */}
              <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900 dark:text-gray-100 leading-tight mb-3">
                {classRoom.name}
              </h1>

              {/* Program link -- points each batch/format page back to the
                  relevant topical hub so ranking signal for the broad
                  "IOE/CSIT entrance preparation" query consolidates there
                  instead of splitting across every classroom variant. */}
              {(() => {
                const lowerName = classRoom.name.toLowerCase();
                if (lowerName.includes("csit")) {
                  return (
                    <Link
                      href="/csit-entrance-preparation"
                      className="inline-flex items-center gap-1 text-xs font-semibold text-primary dark:text-primary-300 hover:underline mb-4 -mt-1"
                    >
                      Part of our BSc CSIT Entrance Preparation program
                      <CaretRight size={9} />
                    </Link>
                  );
                }
                if (lowerName.includes("ioe")) {
                  return (
                    <Link
                      href="/ioe-entrance-preparation"
                      className="inline-flex items-center gap-1 text-xs font-semibold text-primary dark:text-primary-300 hover:underline mb-4 -mt-1"
                    >
                      Part of our IOE Entrance Preparation program
                      <CaretRight size={9} />
                    </Link>
                  );
                }
                return null;
              })()}

              {/* Stats row */}
              <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-sm text-gray-500 dark:text-gray-400 mb-4">
                {previewMeta?.totalSessions ? (
                  <>
                    <span className="font-semibold text-gray-700 dark:text-gray-200">
                      {previewMeta.totalSessions} sessions
                    </span>
                    <span className="text-gray-300 dark:text-gray-600">·</span>
                  </>
                ) : null}
                {previewMeta?.totalHours ? (
                  <>
                    <span>{previewMeta.totalHours}h total</span>
                    <span className="text-gray-300 dark:text-gray-600">·</span>
                  </>
                ) : null}
                {startDate ? (
                  <>
                    <span className="flex items-center gap-1">
                      <CalendarBlank size={12} />
                      Starts {startDate}
                    </span>
                    <span className="text-gray-300 dark:text-gray-600">·</span>
                  </>
                ) : null}
                {freePreviews.length > 0 && (
                  <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
                    {freePreviews.length} free preview{freePreviews.length !== 1 ? "s" : ""}
                  </span>
                )}
              </div>

              {/* Instructor row */}
              <div className="flex items-center gap-3 pb-5 mb-5 border-b border-gray-100 dark:border-gray-800">
                <div
                  className="shrink-0 w-10 h-10 overflow-hidden rounded-xl bg-primary-50 dark:bg-primary-900/30 border border-gray-200 dark:border-gray-700"
                >
                  <Image
                    src={heroLogo}
                    width={40}
                    height={40}
                    alt="Clamphook"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900 dark:text-gray-100 leading-tight">
                    Clamphook Academy
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Nepal&apos;s #1 IOE &amp; CSIT Prep Platform
                  </p>
                </div>
              </div>

              {/* Course overview: Course details, Contacts, Payment Details */}
              <Overview title="Course Overview" classRoom={classRoom} />

              {/* Modules — visible on mobile only (desktop shows in sidebar) */}
              {(curriculum.length > 0 || !curriculumLoaded) && (
                <div className="lg:hidden mt-8">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-base font-semibold text-gray-900 dark:text-gray-100">Course Modules</p>
                    {previewMeta?.totalSessions ? (
                      <span className="text-xs text-gray-500 dark:text-gray-400">{previewMeta.totalSessions} sessions</span>
                    ) : null}
                  </div>
                  <div className="border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden">
                    {!curriculumLoaded ? (
                      <div className="divide-y divide-gray-100 dark:divide-gray-700">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="flex items-center gap-3 px-4 py-3.5">
                            <div className="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-800 animate-pulse shrink-0" />
                            <div className="flex-1 h-3 bg-gray-100 dark:bg-gray-800 animate-pulse rounded" />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <ModuleAccordion
                        curriculum={curriculum}
                        onPreview={(id, title) => playPreview(id, title)}
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Registration — mobile only (desktop uses sidebar) */}
              <div ref={mobileRegRef} className="lg:hidden mt-8 scroll-mt-4">
                <p className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-3">Register</p>
                <div className="border border-gray-200 dark:border-gray-700 rounded-2xl p-5">
                  <Registration
                    title="Registration Form"
                    classRoom={classRoom}
                    onSignIn={handleSignIn}
                  />
                </div>
              </div>
            </div>

            {/* ══ RIGHT SIDEBAR ════════════════════════════════════════════ */}
            <div className="hidden lg:block self-start sticky top-6">
              <div className="border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden shadow-sm dark:bg-gray-800">

                {/* Price + CTA */}
                <div className="p-5 border-b border-gray-100 dark:border-gray-700">
                  {priceDisplay && (
                    <div className="flex items-baseline gap-2 mb-3">
                      <span className="text-3xl font-semibold text-gray-900 dark:text-gray-100">{priceDisplay}</span>
                      {originalPrice && (
                        <span className="text-sm text-gray-600 dark:text-gray-400 line-through">{originalPrice}</span>
                      )}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={scrollToReg}
                    className="w-full bg-primary hover:bg-primary-600 text-white font-bold py-3 rounded-xl text-sm transition-colors mb-2"
                  >
                    Enroll Now
                  </button>
                  <div className="space-y-1">
                    <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                      <Warning size={11} className="text-amber-500 shrink-0" />
                      Once paid, non-refundable
                    </p>
                    {startDate && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                        <CalendarBlank size={11} className="text-primary dark:text-primary-300 shrink-0" />
                        Batch starts {startDate}
                      </p>
                    )}
                  </div>
                  {(classRoom.paymentModes.includes("esewa") ||
                    classRoom.paymentModes.includes("khalti")) && (
                    <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                      <ShieldCheck size={12} className="text-primary dark:text-primary-300 shrink-0" weight="fill" />
                      Pay securely with{" "}
                      {[
                        classRoom.paymentModes.includes("esewa") && "eSewa",
                        classRoom.paymentModes.includes("khalti") && "Khalti",
                      ]
                        .filter(Boolean)
                        .join(" or ")}{" "}
                      &mdash; verified instantly
                    </div>
                  )}
                </div>

                {/* Registration form — priority above modules */}
                <div ref={registrationRef} className="border-b border-gray-100 dark:border-gray-700 scroll-mt-8">
                  <div className="px-5 py-3 bg-primary-50 dark:bg-primary-900/20 border-b border-gray-100 dark:border-gray-700">
                    <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                      Registration
                    </p>
                  </div>
                  <div className="px-5 pb-6 pt-3">
                    <Registration title="Registration Form" classRoom={classRoom} />
                  </div>
                </div>

                {/* Modules — accordion dropdown */}
                <div>
                  <div className="flex items-center justify-between px-5 py-3 bg-primary-50 dark:bg-primary-900/20">
                    <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">Modules</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {previewMeta?.totalSessions
                        ? `${previewMeta.totalSessions} sessions`
                        : `${curriculum.length} batches`}
                    </p>
                  </div>

                  <div className="max-h-80 overflow-y-auto divide-y divide-gray-50 dark:divide-gray-700/50">
                    {!curriculumLoaded ? (
                      <>
                        {[1, 2, 3, 4].map((i) => (
                          <div key={i} className="flex items-center gap-3 px-5 py-3.5">
                            <div className="w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-800 animate-pulse shrink-0" />
                            <div className="flex-1 h-3 bg-gray-100 dark:bg-gray-800 animate-pulse rounded" />
                          </div>
                        ))}
                      </>
                    ) : curriculum.length === 0 ? (
                      <p className="px-5 py-4 text-xs text-gray-600 dark:text-gray-400 text-center">Curriculum coming soon.</p>
                    ) : (
                      <ModuleAccordion
                        curriculum={curriculum}
                        onPreview={(id, title) => playPreview(id, title)}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile sticky enroll bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center gap-3">
        {priceDisplay && (
          <div className="shrink-0">
            <p className="text-base font-semibold text-gray-900 dark:text-gray-100 leading-tight">{priceDisplay}</p>
            {originalPrice && (
              <p className="text-xs text-gray-600 dark:text-gray-400 line-through">{originalPrice}</p>
            )}
          </div>
        )}
        <button
          type="button"
          onClick={scrollToEnroll}
          className="flex-1 bg-primary hover:bg-primary-600 text-white font-bold py-3 rounded-xl text-sm transition-colors"
        >
          Enroll Now
        </button>
      </div>

    </>
  );
};

ClassRoomPage.getLayout = defaultLayout;

export async function getStaticPaths() {
  try {
    const res = await axios.get(getAllClassrooms);
    const classrooms = res.data.data as Classroom[];
    return {
      paths: classrooms.map((c) => ({
        params: { slug: createClassroomSlug(c.name, getClassroomId(c)) },
      })),
      fallback: "blocking",
    };
  } catch {
    return { paths: [], fallback: "blocking" };
  }
}

export const getStaticProps: GetStaticProps = async (props) => {
  try {
    const classroomId = extractIdFromSlug(props.params?.slug as string);
    const [classroomRes, previewRes] = await Promise.allSettled([
      axios(getClassroom + classroomId),
      axios.get(`${getClassroomPreview}${classroomId}/preview`),
    ]);

    const classRoom =
      classroomRes.status === "fulfilled"
        ? classroomRes.value.data.data
        : null;

    let previewMeta: PreviewMeta | null = null;
    let initialCurriculum: Batch[] = [];
    if (previewRes.status === "fulfilled" && previewRes.value.data?.success) {
      const d = previewRes.value.data.data;
      initialCurriculum = d.curriculum ?? [];
      const totalMs = initialCurriculum
        .flatMap((b: any) => b.sessions ?? [])
        .reduce((s: number, se: any) => s + (se.durationSeconds ?? 0), 0);
      // API returns durationSeconds in milliseconds
      const totalHoursNum = totalMs / 3_600_000;
      previewMeta = {
        totalSessions: d.totalSessions ?? 0,
        freePreviewCount: d.freePreviewCount ?? 0,
        totalHours: totalHoursNum > 0.1 ? totalHoursNum.toFixed(1) : "",
      };
    }

    return {
      props: {
        classRoom: classRoom ? normalizeClassroom(classRoom) : null,
        previewMeta,
        initialCurriculum,
      },
      revalidate: 60,
    };
  } catch {
    return { props: { classRoom: null, previewMeta: null, initialCurriculum: [] }, revalidate: 30 };
  }
};

export default ClassRoomPage;
